import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_USER_REVIEWS } from '../../graphql/queries/reviewsQueries';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';

/**
 * Hook for fetching reviews for a user
 * @param {Object} options Options for the hook
 * @param {string} options.userId - User ID for fetching reviews
 * @param {number} options.limit - Number of reviews to fetch (optional)
 * @param {string} options.nextToken - Pagination token (optional)
 * @param {boolean} options.skip - Whether to skip the query (optional)
 * @param {string} options.fetchPolicy - Apollo fetch policy (optional)
 * @returns {Object} Query result containing loading, error, data, and fetchMore function
 */
const useFetchUserReviews = ({
  userId,
  limit = 10,
  nextToken,
  skip = false,
  fetchPolicy = 'cache-and-network',
}) => {
  const variables = { revieweeId: userId, limit, nextToken };
  
  // Skip the query if userId is not provided
  const shouldSkip = skip || !userId;

  const result = useQuery(GET_USER_REVIEWS, {
    variables,
    skip: shouldSkip,
    fetchPolicy,
  });

  const { data, loading, error, fetchMore, refetch } = result;

  // Process reviews with profile pictures
  const processedReviews = useMemo(() => {
    if (!data?.listReviews?.items) {
      return [];
    }

    return (data.listReviews.items || []).map(review => {
      if (review.reviewer && review.reviewer.profilePicture) {
        try {
          const processedPicture = getCloudFrontImageUrl(review.reviewer.profilePicture, { 
            width: 200,
            height: 200, 
            fit: 'cover' 
          });
          return {
            ...review,
            reviewer: {
              ...review.reviewer,
              profilePicture: processedPicture
            }
          };
        } catch (error) {
          console.error('Error processing reviewer profile picture:', error);
          return {
            ...review,
            reviewer: {
              ...review.reviewer,
              profilePicture: ''
            }
          };
        }
      }
      return review;
    });
  }, [data]);

  // Function to fetch more reviews
  const loadMoreReviews = () => {
    const currentNextToken = data?.listReviews?.nextToken;
    if (currentNextToken && !loading) {
      fetchMore({
        variables: {
          revieweeId: userId,
          nextToken: currentNextToken,
          limit,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            listReviews: {
              __typename: prev.listReviews.__typename,
              items: [
                ...prev.listReviews.items,
                ...fetchMoreResult.listReviews.items,
              ],
              nextToken: fetchMoreResult.listReviews.nextToken,
            },
          };
        },
      });
    }
  };

  return {
    reviews: processedReviews,
    loading,
    error,
    hasNextPage: !!data?.listReviews?.nextToken,
    refetch,
    loadMoreReviews,
    ...result,
  };
};

export default useFetchUserReviews;